import { Modal } from '@mui/material';
import { useEffect, useState } from 'react';
import { X } from 'phosphor-react';
import { AppInputField } from '../AppInputField/AppInputField';
import { AppSwitch } from '../AppSwitch/AppSwitch';
import { DillButton } from '../Dill';
import { COLORS } from '../../../utils/colors';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import {
  getSupplierIntegrationDetails,
  saveP21ManualUploadSettingsService,
} from '../../../redux/services/supplierAdditionalDetailsService';

// The P21 web login Dill uses to export the "Dill - ..." reports from My Reports every night.
const AppP21ManualUploadSettingsModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { currentSupplier } = useAppSelector((state) => state.auth);
  const { integrationDetails } = useAppSelector((state) => state.supplierIntegrationDetails);
  const savedSettings = integrationDetails?.p21ManualUpload;

  const [baseUrl, setBaseUrl] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [isAutomatedUploadEnabled, setIsAutomatedUploadEnabled] = useState(false);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setBaseUrl(savedSettings?.baseUrl ?? '');
      setUserName(savedSettings?.userName ?? '');
      setPassword('');
      setIsAutomatedUploadEnabled(!!savedSettings?.isAutomatedUploadEnabled);
      setErrorFields([]);
      setErrorMessage('');
    }
  }, [open, savedSettings]);

  const handleSubmit = async () => {
    const errors: string[] = [];
    if (!baseUrl.trim()) {
      errors.push('baseUrl');
    }
    if (!userName.trim()) {
      errors.push('userName');
    }
    // A saved password is never sent back to the browser, so blank means "keep it".
    if (!password && !savedSettings?.hasPassword) {
      errors.push('password');
    }
    setErrorFields(errors);
    setErrorMessage('');
    if (errors.length > 0 || !currentSupplier) {
      return;
    }

    setIsLoading(true);
    const res = await dispatch(
      saveP21ManualUploadSettingsService({
        supplierId: currentSupplier.id,
        settings: { baseUrl, userName, password, isAutomatedUploadEnabled },
      })
    );
    if (res.meta.requestStatus === 'fulfilled') {
      await dispatch(getSupplierIntegrationDetails({ supplierId: currentSupplier.id }));
      setIsLoading(false);
      handleClose();
    } else {
      setIsLoading(false);
      setErrorMessage((res.payload as string) || 'Failed to save P21 settings');
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="quick-books-auth-content text-center">
        <div className="flex flex-row mb-4">
          <DillButton
            variant="ghost"
            className="absolute top-5 right-5"
            icon={<X color={COLORS.GREY_300} size={24} />}
            onClick={handleClose}
          />
        </div>
        <h1 className="text-2xl leading-normal font-bold">P21 Automated Upload</h1>
        <p>
          Dill signs in to P21 with this login every night and imports the Dill reports from My
          Reports.
        </p>
        <div className="flex flex-col gap-5 w-full mt-4 text-left">
          <AppInputField
            id="p21BaseUrl"
            isRequired={true}
            errorText={errorFields.includes('baseUrl') ? 'P21 URL is required' : ''}
            label="P21 URL"
            placeholder="https://yourcompany.epicordistribution.com/Prophet21/#/login"
            inputType="text"
            value={baseUrl}
            onTextChange={(value: string) => setBaseUrl(value)}
          />
          <AppInputField
            id="p21UserName"
            isRequired={true}
            errorText={errorFields.includes('userName') ? 'Username is required' : ''}
            label="Username"
            placeholder="Username"
            inputType="text"
            value={userName}
            onTextChange={(value: string) => setUserName(value)}
          />
          <AppInputField
            id="p21Password"
            isRequired={!savedSettings?.hasPassword}
            errorText={errorFields.includes('password') ? 'Password is required' : ''}
            label="Password"
            placeholder={
              savedSettings?.hasPassword ? 'Leave blank to keep the saved password' : 'Password'
            }
            inputType="password"
            value={password}
            onTextChange={(value: string) => setPassword(value)}
          />
          <div className="flex flex-row items-center justify-between">
            <p className="text-sm font-medium">Run automated upload every night</p>
            <AppSwitch
              isChecked={isAutomatedUploadEnabled}
              onSwitchChange={(value: boolean) => setIsAutomatedUploadEnabled(value)}
            />
          </div>
          {errorMessage && <p className="text-sm ERROR_500-CLR">{errorMessage}</p>}
        </div>
        <div className="flex flex-row gap-4 justify-center mt-6">
          <DillButton
            label="Cancel"
            variant="secondary"
            isDisabled={isLoading}
            onClick={handleClose}
          />
          <DillButton label="Save" isLoading={isLoading} onClick={handleSubmit} />
        </div>
      </div>
    </Modal>
  );
};

export default AppP21ManualUploadSettingsModal;
